import { ReactNode, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import {
  AddOpacityToBackground,
  cleanParagraphTags,
  IsNodeStructure,
} from "@talladega/helpers";
import { ActionButton, FeatureBannerLayout } from "@talladega/types";
import { CmsHeading } from "../CmsHeading";

type Props = {
  layout: FeatureBannerLayout;
  cmsButton: (
    setButtonModalState: React.Dispatch<
      React.SetStateAction<{
        show: boolean;
        actionType: ActionButton;
      }>
    >
  ) => ReactNode;
  bgCloudflareImage: (isInView: boolean) => ReactNode;
  iconCloudflareImage: ReactNode;
  cmsButtonModalContainer: (buttonModalState: {
    show: boolean;
    actionType: ActionButton;
  }) => ReactNode;
};

export const FeatureBanner = ({
  layout,
  cmsButton,
  bgCloudflareImage,
  iconCloudflareImage,
  cmsButtonModalContainer,
}: Props) => {
  const { heading, subheading } = layout;
  const [buttonModalState, setButtonModalState] = useState<{
    show: boolean;
    actionType: ActionButton;
  }>({ show: false, actionType: ActionButton.UnAssigned });

  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  const { sourceUrl: iconSourceUrl } =
    (IsNodeStructure(layout.container.icon)
      ? layout.container?.icon?.node
      : layout.container?.icon) ?? {};

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(entry.isIntersecting);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.9, // 90% of the element is visible
      }
    );

    if (ref.current && !isInView) {
      observer.observe(ref.current);
    }

    const currentRef = ref.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isInView, ref]);

  const backgroundStyle = () => {
    if (layout.container.background.backgroundOption === "Solid") {
      return {
        backgroundColor: layout.container.background.color ?? "#000000",
      };
    }
    if (layout.container.background.backgroundOption === "Transparent") {
      return {
        backgroundColor: AddOpacityToBackground(
          layout.container.background.color ?? "#000000",
          layout.container.background.opacity ?? 0.55
        ),
      };
    }
    return {};
  };

  return (
    <div
      id="cms-feature-banner"
      ref={ref}
      className={classNames(
        "no-scrollbar relative mx-auto overflow-visible bg-white transition-all delay-150 duration-500",
        {
          "max-w-7xl scale-90": !isInView,
          "max-w-full scale-100": isInView,
        }
      )}
    >
      <div className="mx-auto max-w-page">
        {/* bgCloudflareImage decoupled */}
        {bgCloudflareImage(isInView)}
        <article className="px-4 pb-6 lg:px-5">
          <div
            className={classNames(
              "md:text-center pt-8 transition-all delay-500 duration-500",
              {
                "translate-y-0 transform": isInView,
                "translate-y-10 transform opacity-0": !isInView,
              }
            )}
          >
            {heading && (
              <CmsHeading
                headingLevel={heading.headingGroup?.headingLevel}
                textSize={heading.headingGroup?.textSize}
                text={heading.headingGroup?.text}
                htmlId={heading.headingGroup?.htmlId}
              />
            )}
            {subheading && (
              <div className="mt-4 lg:mt-8">
                <CmsHeading
                  headingLevel={subheading.headingGroup?.headingLevel}
                  textSize={subheading.headingGroup?.textSize}
                  text={subheading.headingGroup?.text}
                  htmlId={subheading.headingGroup?.htmlId}
                />
              </div>
            )}
          </div>
        </article>
        <div
          className={classNames(
            "flex h-full flex-row justify-center px-2 py-16 text-center transition-all delay-500 duration-500 sm:text-left md:px-36",
            {
              "translate-y-0 transform": isInView,
              "translate-y-10 transform opacity-0": !isInView,
              "md:justify-start": layout.container.position === "Left",
              "md:justify-end": layout.container.position === "Right",
            }
          )}
        >
          <div
            className="flex h-full w-fit max-w-sm flex-col rounded-xl p-8"
            style={backgroundStyle()}
          >
            {layout.container.icon && iconSourceUrl && (
              <div className="mb-3 flex w-full justify-center">
                <div className="relative h-16 w-16">
                  {/* iconCloudflareImage decoupled */}
                  {iconCloudflareImage}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-y-2 pb-12">
              <h3
                className="text-4xl"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: cleanParagraphTags(layout.container.heading),
                }}
              />
              <div
                className="text-md font-normal"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: cleanParagraphTags(layout.container.body),
                }}
              />
            </div>
            {layout.container.button && !layout.container.button.isHidden && (
              <>
                {/* cmsButton decoupled */}
                {cmsButton(setButtonModalState)}
              </>
            )}
          </div>
        </div>
        {/* cmsButtonModalContainer decoupled */}
        {buttonModalState.show && cmsButtonModalContainer(buttonModalState)}
      </div>
    </div>
  );
};

export default {};
